import { useTranslation } from "react-i18next";
import { FormProvider, FieldErrors } from "react-hook-form";
import React, { useState } from "react";
import { Icon } from "@ucl/dse/icons";
import { Spinner } from "@ucl/dse/spinner";
import { TextInput } from "@ucl/dse/textInput";
import { Button } from "@ucl/dse/button";
import { Text } from "@ucl/dse/text";
import { CurrencyInput } from "@ucl/dse/currencyInput";
import AddressSection from "src/components/address-section/address-section";
import { useErrorSummary } from "src/hooks/use-error-summary";
import { LinkButton } from "src/components/link-button/link-button";
import { LinkType, EligibilityResultType } from "src/types/general";
import { useFormPageSetup } from "./form-page-setup";
import { DOBInput } from "src/components/dob-input/dob-input";
import { useAdobeAnalytics } from "src/hooks/use-adobe-analytics";
import { handleValidFormSubmit, handleInvalidFormSubmit } from "./form-page-utils";
import styles from "./form-page.module.scss";

export interface FormData {
    firstName: string;
    lastName: string;
    email: string;
    dobDay: string;
    dobMonth: string;
    dobYear: string;
    dobCombined?: string;
    income: string;
    currentAddress: {
        flatNumber?: string;
        houseNumber?: string;
        houseName?: string;
        street: string;
        townCity: string;
        county: string;
        postcode: string;
        month: string;
        year: string;
    };
    previousAddress: {
        flatNumber?: string;
        houseNumber?: string;
        houseName?: string;
        street?: string;
        townCity?: string;
        county?: string;
        postcode?: string;
        month?: string;
        year?: string;
    };
}

interface FormPageProps {
    correlationId: string;
    cookiePreferences: boolean;
    onSubmitSuccess: (result: EligibilityResultType) => void;
}

const FormPage: React.FC<FormPageProps> = ({ correlationId, cookiePreferences, onSubmitSuccess }) => {
    const { t } = useTranslation();
    const { methods, errors, firstNameProps, lastNameProps, emailProps, incomeProps } = useFormPageSetup(t);
    const { trackFormErrorsOnSubmission } = useAdobeAnalytics(cookiePreferences);

    const [fetching, setFetching] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>();

    const privacyPolicyConfig = t("privacyPolicy", { returnObjects: true }) as {
        label: string;
        linkProps: LinkType;
    };

    const errorSummary = useErrorSummary({ errors, submitError });

    const onValidFormSubmit = (formData: FormData) => {
        handleValidFormSubmit({
            correlationId,
            formData,
            setFetching,
            setSubmitError,
            onSubmitSuccess: (result) => onSubmitSuccess(result),
        });
    };

    const onInvalidFormSubmit = (formErrors: FieldErrors<FormData>) => {
        handleInvalidFormSubmit({ errors: formErrors, trackFormErrorsOnSubmission });
    };

    return (
        <FormProvider {...methods}>
            <>
                <form
                    className={styles.formContainer}
                    noValidate
                    onSubmit={methods.handleSubmit(onValidFormSubmit, onInvalidFormSubmit)}
                    aria-busy={fetching}
                >
                    <Text.H1 data-testid="heading" fontWeight="semi-bold">
                        {t("personalDetails.heading")}
                    </Text.H1>
                    <section className={styles.formSection}>
                        <Text.H2
                            className={styles.sectionHeading}
                            data-testid="subheadingAboutYou"
                            fontWeight="semi-bold"
                        >
                            {t("personalDetails.aboutYou")}
                        </Text.H2>

                        <div className={styles.nameFieldsWrapper}>
                            <TextInput
                                data-testid="firstName"
                                autoComplete="given-name"
                                inputMode="text"
                                labelProp={{ label: t("personalDetails.labels.firstName") }}
                                placeholder=""
                                maxLength={15}
                                required={true}
                                {...firstNameProps}
                            />
                            <TextInput
                                data-testid="lastName"
                                autoComplete="family-name"
                                inputMode="text"
                                labelProp={{ label: t("personalDetails.labels.lastName") }}
                                placeholder=""
                                maxLength={30}
                                required={true}
                                {...lastNameProps}
                            />
                        </div>

                        <div className={styles.emailFieldWrapper}>
                            <TextInput
                                data-testid="email"
                                autoComplete="email"
                                inputMode="email"
                                labelProp={{ label: t("personalDetails.labels.email") }}
                                placeholder=""
                                maxLength={60}
                                required={true}
                                {...emailProps}
                            />
                            <Text.P className={styles.emailDisclaimer}>{t("personalDetails.emailDisclaimer")}</Text.P>
                        </div>

                        <DOBInput t={t} control={methods.control} errors={errors} />
                    </section>

                    <AddressSection />

                    <section className={styles.formSection}>
                        <Text.H2
                            className={styles.sectionHeading}
                            data-testid="subheadingIncomePrompt"
                            fontWeight="semi-bold"
                        >
                            {t("personalDetails.incomePrompt")}
                        </Text.H2>
                        <div className={styles.incomeFieldWrapper}>
                            <CurrencyInput
                                data-testid="income"
                                autoComplete="off"
                                inputMode="numeric"
                                labelProp={{ label: t("personalDetails.labels.income") }}
                                placeholder=""
                                inputAlignment={"left"}
                                currencyCode={"GBP"}
                                isRequired={true}
                                {...incomeProps}
                            />
                        </div>
                    </section>

                    <section className={styles.formSection}>
                        <div className={styles.infoBox}>
                            <div className={styles.infoBoxIcon}>
                                <Icon icon="padlockClosed" size="xxl" color="default" />
                            </div>
                            <div className={styles.infoBoxText}>
                                <Text.P>{t("personalDetails.softCheckText")}</Text.P>
                            </div>
                        </div>

                        <div data-testid="privacyPolicySection">
                            <Text.H3 className={styles.importantInfoHeader} fontWeight="semi-bold">
                                {t("personalDetails.importantInfoHeader")}
                            </Text.H3>
                            <Text.P>
                                {t("personalDetails.submissionAgreement") + " "}
                                <LinkButton
                                    config={privacyPolicyConfig.linkProps}
                                    ariaLabel={privacyPolicyConfig.label + " " + t("general.openNewTabAriaLabel")}
                                    variant="tertiary"
                                    style={{
                                        display: "inline",
                                        padding: "0",
                                        textDecoration: "underline",
                                        textUnderlineOffset: "4px",
                                    }}
                                >
                                    {privacyPolicyConfig.label}
                                </LinkButton>
                                {"."}
                            </Text.P>
                        </div>

                        <div data-testid="importantInfo">
                            <Text.P>{t("personalDetails.importantInfo")}</Text.P>
                        </div>

                        {errorSummary}

                        <div className={styles.buttonWrapper}>
                            <Button
                                data-testid="submitButton"
                                type="submit"
                                disabled={fetching}
                                className={styles.submitButton}
                            >
                                {t("personalDetails.continueButton")}
                            </Button>
                        </div>
                    </section>
                </form>
                {fetching && (
                    <div
                        data-testid="loadingSpinner"
                        className={styles.loadingOverlay}
                        role="status"
                        aria-live="polite"
                        aria-label={t("submittingSpinner.ariaLabel")}
                    >
                        <div className={styles.loadingContent}>
                            <Spinner size="large" className={styles.spinner} />
                            <Text.P fontWeight="bold" size="large">
                                {t("submittingSpinner.heading")}
                            </Text.P>
                            <Text.P fontWeight="medium" size="large">
                                {t("submittingSpinner.duration")}
                            </Text.P>
                            <Text.P fontWeight="medium" size="large">
                                {t("submittingSpinner.instruction")}
                            </Text.P>
                        </div>
                    </div>
                )}
            </>
        </FormProvider>
    );
};

export default FormPage;
